import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./FontCreator.scss";

import baseLayout from "../fontCreator/layout.json";
import converterUrl from "../fontCreator/converter.py";
import webUrl from "../fontCreator/web.py";

/* ============================ DATA ============================ */

const TEMPLATE_URL =
  "https://drive.google.com/uc?export=download&id=1YNExpmj0pdQTRdSrPYCRmSs_mS2YUpl7";

/* resolved on its own line so webpack emits the file as-is instead of
   bundling it as a classic worker */
const workerUrl = new URL("../fontCreator/fontWorker.js", import.meta.url);

const EXTENSIONS = [".png", ".jpg", ".jpeg", ".tif", ".tiff", ".bmp", ".webp"];
const MAX_NAME = 40;
const LETTERS = baseLayout.rows.reduce((n, r) => n + r.characters.length, 0);

const DEFAULT_TEXT = "The quick brown fox\njumps over the lazy dog";
const DEFAULT_SETTINGS = {
  family_name: "My Handwriting",
  side_bearing: baseLayout.side_bearing,
  space_width: baseLayout.space_width,
  threshold: baseLayout.threshold,
  normalize_baseline: true,
  italic: false,
  slant: 12,
};

/* the converter takes one slant angle — 0 is upright */
const toLayout = ({ italic, slant, ...rest }) => ({
  ...baseLayout,
  ...rest,
  slant: italic ? slant : 0,
});

/* font units / darkness cutoff — the template's grey guide letters sit at
   221, so the cutoff stops short of picking them up */
const SLIDERS = [
  { key: "side_bearing", label: "Letter spacing", min: 0,   max: 200, step: 5 },
  { key: "space_width",  label: "Space width",    min: 100, max: 600, step: 10 },
  { key: "threshold",    label: "Ink sensitivity", min: 80,  max: 210, step: 5 },
];

/* The anchor has to be in the document for Firefox to honour it, and
   revoking the URL in the same tick can cut the download off before the
   browser has read the blob. */
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const extensionOf = (name) => {
  const m = /\.[a-z0-9]+$/i.exec(name || "");
  return m ? m[0].toLowerCase() : "";
};

/* ============================ PAGE ============================ */

export default function FontCreator() {
  const [engine, setEngine] = useState("loading"); // loading | ready | error
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [sheet, setSheet] = useState(null); // { name, extension, bytes }
  const [building, setBuilding] = useState(false);
  const [traced, setTraced] = useState(0);
  const [font, setFont] = useState(null); // { family, stem, characters, ttf, woff2, settings }
  const [notice, setNotice] = useState(null); // { kind: "ok" | "error", text }
  const [text, setText] = useState(DEFAULT_TEXT);
  const [size, setSize] = useState(64);
  const [dragOver, setDragOver] = useState(false);
  const workerRef = useRef(null);
  const buildIdRef = useRef(0);
  const pendingRef = useRef(null); // settings of the build in flight
  const faceRef = useRef(null);
  const fileRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const worker = new Worker(workerUrl, { type: "module" });
    workerRef.current = worker;

    worker.onmessage = async ({ data }) => {
      if (data.type === "ready") setEngine("ready");
      if (data.type === "bootError") {
        setEngine("error");
        setBuilding(false);
        setNotice({
          kind: "error",
          text: "The font engine could not load. Check your connection and refresh.",
        });
        return;
      }
      if (data.type === "traced") setTraced((n) => n + 1);
      if (data.id !== buildIdRef.current) return;

      if (data.type === "buildError") {
        setBuilding(false);
        setNotice({ kind: "error", text: data.message });
      }
      if (data.type === "built") {
        /* a fresh family name per build, so the browser never shows a
           cached copy of the previous font */
        const family = `fc-preview-${data.id}`;
        try {
          const face = new FontFace(family, data.ttf);
          await face.load();
          if (faceRef.current) document.fonts.delete(faceRef.current);
          document.fonts.add(face);
          faceRef.current = face;
        } catch (err) {
          setBuilding(false);
          setNotice({ kind: "error", text: "The browser could not load the new font." });
          return;
        }
        setFont({
          family,
          stem: data.stem,
          characters: data.characters,
          ttf: data.ttf,
          woff2: data.woff2,
          settings: pendingRef.current,
        });
        setBuilding(false);
        setNotice({ kind: "ok", text: "Font ready — start typing." });
        if (textRef.current) textRef.current.focus();
      }
    };
    worker.postMessage({ type: "init", converterUrl, webUrl });

    return () => {
      worker.terminate();
      if (faceRef.current) document.fonts.delete(faceRef.current);
    };
  }, []);

  /* -------- building -------- */

  const build = (source, using) => {
    const name = using.family_name.trim();
    if (!name) {
      setNotice({ kind: "error", text: "Give your font a name first." });
      return;
    }
    const id = ++buildIdRef.current;
    const chosen = { ...using, family_name: name };
    setTraced(0);
    setBuilding(true);
    setNotice(null);
    pendingRef.current = chosen;
    workerRef.current.postMessage({
      type: "build",
      id,
      extension: source.extension,
      image: source.bytes, // copied, so the sheet can be rebuilt later
      layout: toLayout(chosen),
    });
  };

  const acceptFile = async (file) => {
    if (!file) return;
    const extension = extensionOf(file.name);
    if (!EXTENSIONS.includes(extension)) {
      setNotice({ kind: "error", text: "Upload a PNG, JPEG, TIFF, BMP, or WebP image." });
      return;
    }
    const source = { name: file.name, extension, bytes: await file.arrayBuffer() };
    setSheet(source);
    build(source, settings);
  };

  const onUpload = (e) => {
    acceptFile(e.target.files[0]);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files[0]);
  };

  const setSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  /* a toggle is a whole decision, so it rebuilds straight away — sliders
     and the name wait for the Rebuild button */
  const setToggle = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    if (sheet && !building && next.family_name.trim()) build(sheet, next);
  };

  const reset = () => {
    buildIdRef.current += 1; // drop any build still in flight
    if (faceRef.current) document.fonts.delete(faceRef.current);
    faceRef.current = null;
    setSettings(DEFAULT_SETTINGS);
    setSheet(null);
    setFont(null);
    setBuilding(false);
    setNotice(null);
    setText(DEFAULT_TEXT);
  };

  const stale =
    font &&
    Object.keys(settings).some(
      (k) => String(settings[k]).trim() !== String(font.settings[k])
    );

  const status = building
    ? engine === "loading"
      ? "Loading font engine…"
      : `Tracing letters… ${Math.min(traced, LETTERS)} / ${LETTERS}`
    : null;

  /* ============================ RENDER ============================ */

  return (
    <>
    <Helmet>
      {/* Title & Description */}
      <title>Handwriting Font Maker | Free Font Creator Tool</title>
      <meta name="description" content="Free online handwriting font maker. Download the template, write your letters, upload the sheet, and get a TTF and WOFF2 font with a live typing preview. No signup." />
      <link rel="canonical" href="https://dylandalal.com/font" />

      {/* Open Graph (Facebook, LinkedIn) */}
      <meta property="og:title" content="Handwriting Font Maker — Free Font Creator Tool" />
      <meta property="og:description" content="Turn your handwriting into a real font in your browser. Free, no signup, exports TTF and WOFF2." />
      <meta property="og:image" content="https://dylandalal.com/favicon.png" />
      <meta property="og:url" content="https://dylandalal.com/font" />
      <meta property="og:type" content="website" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Handwriting Font Maker — Free Font Creator Tool" />
      <meta name="twitter:description" content="Turn your handwriting into a real font in your browser. Free, no signup, exports TTF and WOFF2." />
      <meta name="twitter:image" content="https://dylandalal.com/favicon.png" />

      {/* Schema Markup */}
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Handwriting Font Maker",
          "url": "https://dylandalal.com/font",
          "applicationCategory": "DesignApplication",
          "operatingSystem": "Any",
          "browserRequirements": "Requires JavaScript",
          "description": "A free browser tool that turns a handwriting template into a TTF and WOFF2 font, with a live typing preview.",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "author": {
            "@type": "Person",
            "name": "Dylan Dalal",
            "url": "https://dylandalal.com"
          }
        }
        `}
      </script>
    </Helmet>

    <section className="fc">
      <header className="fc__head">
        <span className="fc__eyebrow">Free Tool</span>
        <h1 className="fc__title">Handwriting Font Maker</h1>
        <p className="fc__sub">
          Download the template, write each letter over its grey guide in a
          dark color, and upload the sheet. Your font is traced right in the
          browser — type in the preview to try it, then download it.
        </p>
      </header>

      <div className="fc__grid">
        {/* ============ LIVE PREVIEW ============ */}
        <div className="fc__preview">
          <div className="fc__controls">
            <label className="fc__slider">
              <span>Preview size</span>
              <input
                type="range"
                min="16"
                max="160"
                step="2"
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
              />
              <span className="fc__sliderVal">{size}</span>
            </label>
            <button
              type="button"
              className="fc__btn fc__btn--ghost fc__btn--mini"
              onClick={() => setText("ABCDEFGHIJKLMNOPQRSTUVWXYZ\nabcdefghijklmnopqrstuvwxyz")}
            >
              Alphabet
            </button>
            <button
              type="button"
              className="fc__btn fc__btn--ghost fc__btn--mini"
              onClick={() => setText(DEFAULT_TEXT)}
            >
              Pangram
            </button>
            <span className="fc__fontName">
              {font ? `${font.settings.family_name} · ${font.characters.length} letters` : "No font yet"}
            </span>
          </div>

          <p className="fc__stageHint">
            {font
              ? "Click in the box and type — the preview is your font."
              : "Upload your sheet to see your handwriting here."}
          </p>

          <textarea
            ref={textRef}
            className={`fc__stage ${font ? "is-live" : ""} ${building ? "is-building" : ""}`}
            style={{
              fontFamily: font ? `"${font.family}", "Futura", system-ui, sans-serif` : undefined,
              fontSize: `${size}px`,
            }}
            value={text}
            spellCheck="false"
            aria-label="Font preview text"
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {/* ============ EDITOR ============ */}
        <aside className="fc__editor">
          <div className="fc__group">
            <div className="fc__groupRule">
              <span>1 · Template</span>
              <span className="fc__groupLine" />
            </div>
            <a className="fc__btn fc__btn--wide" href={TEMPLATE_URL} download="Font Template.png">
              Download Template
            </a>
            <p className="fc__hint">
              Write over the grey guides in black or another dark color, in
              any drawing app or on paper. Keep every letter inside its own
              area and don't crop or stretch the sheet.
            </p>
          </div>

          <div className="fc__group">
            <div className="fc__groupRule">
              <span>2 · Your sheet</span>
              <span className="fc__groupLine" />
            </div>
            <button
              type="button"
              className={`fc__drop ${dragOver ? "is-over" : ""}`}
              onClick={() => fileRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              disabled={engine === "error"}
            >
              <span className="fc__dropTitle">
                {sheet ? "Upload a different sheet" : "Upload your sheet"}
              </span>
              <span className="fc__dropSub">
                {sheet ? sheet.name : "Click, or drop the image here"}
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={EXTENSIONS.join(",")}
              onChange={onUpload}
              hidden
            />
            {status && (
              <div className="fc__progress">
                <span>{status}</span>
                <span className="fc__bar">
                  <span
                    className="fc__barFill"
                    style={{ width: `${(Math.min(traced, LETTERS) / LETTERS) * 100}%` }}
                  />
                </span>
              </div>
            )}
            {notice && (
              <p className={`fc__notice fc__notice--${notice.kind}`}>{notice.text}</p>
            )}
          </div>

          <div className="fc__group">
            <div className="fc__groupRule">
              <span>3 · Settings</span>
              <span className="fc__groupLine" />
            </div>
            <label className="fc__field">
              <span className="fc__rowName">Font name</span>
              <input
                type="text"
                className="fc__input"
                value={settings.family_name}
                maxLength={MAX_NAME}
                placeholder="My Handwriting"
                onChange={(e) => setSetting("family_name", e.target.value)}
              />
            </label>
            {SLIDERS.map((s) => (
              <label className="fc__slider fc__slider--row" key={s.key}>
                <span>{s.label}</span>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={settings[s.key]}
                  onChange={(e) => setSetting(s.key, Number(e.target.value))}
                />
                <span className="fc__sliderVal">{settings[s.key]}</span>
              </label>
            ))}
            <label className="fc__check">
              <input
                type="checkbox"
                checked={settings.normalize_baseline}
                disabled={building}
                onChange={(e) => setToggle("normalize_baseline", e.target.checked)}
              />
              <span>Normalize baseline</span>
            </label>
            <label className="fc__check">
              <input
                type="checkbox"
                checked={settings.italic}
                disabled={building}
                onChange={(e) => setToggle("italic", e.target.checked)}
              />
              <span>Italicize</span>
            </label>
            {settings.italic && (
              <label className="fc__slider fc__slider--row">
                <span>Slant</span>
                <input
                  type="range"
                  min="2"
                  max="30"
                  step="1"
                  value={settings.slant}
                  onChange={(e) => setSetting("slant", Number(e.target.value))}
                />
                <span className="fc__sliderVal">{settings.slant}°</span>
              </label>
            )}
            <button
              type="button"
              className={`fc__btn fc__btn--wide ${stale ? "fc__btn--accent" : ""}`}
              onClick={() => build(sheet, settings)}
              disabled={!sheet || building}
            >
              {stale ? "Rebuild with new settings" : "Rebuild font"}
            </button>
          </div>

          <div className="fc__group">
            <div className="fc__groupRule">
              <span>4 · Download</span>
              <span className="fc__groupLine" />
            </div>
            <div className="fc__toolbar">
              <button
                type="button"
                className="fc__btn fc__btn--accent"
                disabled={!font}
                onClick={() => saveBlob(new Blob([font.ttf], { type: "font/ttf" }), `${font.stem}.ttf`)}
              >
                Download TTF
              </button>
              <button
                type="button"
                className="fc__btn"
                disabled={!font}
                onClick={() => saveBlob(new Blob([font.woff2], { type: "font/woff2" }), `${font.stem}.woff2`)}
              >
                Download WOFF2
              </button>
              <button type="button" className="fc__btn fc__btn--ghost" onClick={reset}>
                Reset
              </button>
            </div>
          </div>

          <p className="fc__hint">
            Everything runs in your browser — your sheet is never uploaded to
            a server. Normalize baseline sits every letter on the same line
            and keeps only true descenders (like g, j, p, q, y) below it.
            The font covers A–Z, a–z, and spaces; numbers and
            punctuation fall back to another font. Install the TTF on your
            computer, or use the WOFF2 on a website.
          </p>
        </aside>
      </div>
    </section>
    </>
  );
}
